import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./UkpnNewEntry.css";

function UkpnNewEntry() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    month: "",
    priorities: "",
    area: "",
    siteDetails: "",
    postCode: "",
    w3w: "",
    budgetHolder: "",
    dateVisited: "",
    comments2025: "",
    additionalInfoComments: ""
  });
  
  const [fieldErrors, setFieldErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (name === "month") {
      setForm({
        ...form,
        [name]: value,
      });
    } else {
      setForm({ 
        ...form, 
        [name]: value 
      });
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous field errors
    setFieldErrors({});
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found. Please login again.");
        return;
      }
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${baseUrl}/api/ukpn`, form, config);
      toast.success("UKPN entry submitted successfully");
      navigate("/admin/ukpn/entry-data");
    } catch (err) {
      console.error("Error:", err);
      
      // Handle validation errors from backend
      if (err.response && err.response.data) {
        // Handle other field-specific errors
        if (err.response.data.field) {
          const { field, message } = err.response.data;
          setFieldErrors({ [field]: message });
          
          // Focus on the problematic field instead of showing toast
          setTimeout(() => {
            const fieldElement = document.querySelector(`[name="${field}"]`);
            if (fieldElement) {
              fieldElement.focus();
              fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
      }
      
      toast.error("Failed to submit UKPN entry");
    }
  };

  return (
    <div className="ukpn-container">
      <div className="ukpn-form">
        <div className="form-header">
          <h1 className="form-title">UKPN</h1>
          <h2 className="form-subtitle">New Entry</h2>
        </div>

        <form onSubmit={handleSubmit} className="ukpn-form-content">
          {/* Section 1 - Site Information */}
          <div className="form-section">
            <h3 className="section-title">Site Information</h3>
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">Month *</label>
                <DatePicker
                  selected={form.month ? new Date(form.month + '-01') : null}
                  onChange={(date) => {
                    if (date) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const monthValue = `${year}-${month}`;
                      setForm({ ...form, month: monthValue });
                      if (fieldErrors.month) {
                        setFieldErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.month;
                          return newErrors;
                        });
                      }
                    }
                  }}
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  placeholderText="Select Month"
                  className={`form-input ${fieldErrors.month ? 'error' : ''}`}
                  required
                  wrapperClassName="month-picker-wrapper"
                  calendarClassName="month-picker-calendar"
                  minDate={new Date(2024, 0, 1)}
                  maxDate={new Date(2026, 11, 31)}
                />
                {fieldErrors.month && (
                  <div className="field-error">{fieldErrors.month}</div>
                )}
              </div>
              
              <div className="form-field">
                <label className="field-label">Priorities *</label>
                <select
                  name="priorities"
                  value={form.priorities}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.priorities ? 'error' : ''}`}
                  required
                >
                  <option value="">Select Priority</option>
                  <option value="5 Hours">5 Hours</option>
                  <option value="24 Hours">24 Hours</option>
                  <option value="7 Days">7 Days</option>
                </select>
                {fieldErrors.priorities && (
                  <div className="field-error">{fieldErrors.priorities}</div>
                )}
              </div>
              
              <div className="form-field">
                <label className="field-label">Area *</label>
                <input
                  type="text"
                  name="area"
                  value={form.area}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.area ? 'error' : ''}`}
                  placeholder="Enter Area"
                  required
                />
                {fieldErrors.area && (
                  <div className="field-error">{fieldErrors.area}</div>
                )}
              </div>
              
              <div className="form-field">
                <label className="field-label">Site Details *</label>
                <input
                  type="text"
                  name="siteDetails"
                  value={form.siteDetails}
                  onChange={handleChange}
                  className={`form-input ${fieldErrors.siteDetails ? 'error' : ''}`}
                  placeholder="Enter Site Details"
                  required
                />
                {fieldErrors.siteDetails && (
                  <div className="field-error">{fieldErrors.siteDetails}</div>
                )}
              </div>
              
              <div className="form-field">
                <label className="field-label">Post Code</label>
                <input
                  type="text"
                  name="postCode"
                  value={form.postCode}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Post Code"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label className="field-label">W3W (What3Words)</label>
                <input
                  type="text"
                  name="w3w"
                  value={form.w3w}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter What3Words code"
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Budget Holder</label>
                <input
                  type="text"
                  name="budgetHolder"
                  value={form.budgetHolder}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter Budget Holder"
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Date Visited</label>
                <DatePicker
                  selected={form.dateVisited ? new Date(form.dateVisited) : null}
                  onChange={(date) => {
                    if (date) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const dateValue = `${year}-${month}-${day}`;
                      handleChange({ target: { name: 'dateVisited', value: dateValue } });
                    } else {
                      handleChange({ target: { name: 'dateVisited', value: '' } });
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select Date Visited"
                  className="form-input schedule-date-picker"
                  wrapperClassName="schedule-date-wrapper"
                  calendarClassName="schedule-date-calendar"
                />
              </div>
            </div>
          </div>

          {/* Section 3 - Comments */}
          <div className="form-section">
            <h3 className="section-title">Comments</h3>
            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Comments made</label>
                <textarea
                  name="comments2025"
                  value={form.comments2025}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Enter comments made in 2025"
                  rows="4"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field full-width">
                <label className="field-label">Additional Info/Comments from previous visits</label>
                <textarea
                  name="additionalInfoComments"
                  value={form.additionalInfoComments}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Enter additional info or comments from previous visits"
                  rows="4"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" className="submit-btn">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UkpnNewEntry;
